import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { caseStudies } from '../data/profile';
import { Reveal } from './Reveal';
import { SectionLabel } from './SectionLabel';
const stages = [{
  key: 'challenge',
  label: 'Situation'
}, {
  key: 'action',
  label: 'Action'
}, {
  key: 'outcome',
  label: 'Outcome'
}] as const;
export function CaseStudies({
  showLabel = true
}: {
  showLabel?: boolean;
}) {
  const [activeId, setActiveId] = useState(caseStudies[0].id);
  const active = caseStudies.find(study => study.id === activeId) ?? caseStudies[0];
  const reduceMotion = useReducedMotion();
  return <section id="results" className="w-full border-t border-line bg-white py-20 lg:py-28" data-id="element-142" data-mp="1dpv0be7dp79a">
      <div className="mx-auto max-w-shell px-6 sm:px-8" data-id="element-143" data-mp="1ino3rzharn5v">
        {showLabel && <Reveal data-id="element-144" data-mp="1nok8gf9h3dcc">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-145" data-mp="1np3r5y6y65rf">
              <div data-id="element-146" data-mp="1smlrx1at9eoc">
                <SectionLabel data-id="element-147" data-mp="1sjzkd1nx6z64">Documented Impact</SectionLabel>
                <h2 className="mt-6 max-w-xl font-serif text-[32px] font-semibold leading-[1.12] tracking-[-0.015em] text-navy sm:text-4xl lg:text-[44px]" data-id="element-148" data-mp="3d8t34btdudab">
                  What the work actually produced
                </h2>
              </div>
              <p className="max-w-sm text-[15px] leading-relaxed text-charcoal-muted" data-id="element-149" data-mp="2z88lhiz7enie">
                Three enterprise programs at MetLife, Syneos Health, and Parexel — each shown as the
                situation, the action taken, and the outcome delivered.
              </p>
            </div>
          </Reveal>}

        <Reveal delay={0.05} data-id="element-150" data-mp="3m86dd6pbz5w3">
          <div className={showLabel ? 'mt-14' : ''} data-id="element-151" data-mp="3m7deerwm5koi">
            <div className="grid grid-cols-1 border border-line lg:grid-cols-[0.36fr_0.64fr]" data-id="element-152" data-mp="3i7cqmgnmjx4s">
              <ul className="border-b border-line lg:border-b-0 lg:border-r" role="tablist" aria-label="Documented impact" data-id="element-153" data-mp="ijcuy0fuuij4">
                {caseStudies.map(study => {
                const isActive = study.id === activeId;
                return <li key={study.id} data-id="element-154" data-mp="2d0357w0dnhub">
                      <button type="button" role="tab" aria-selected={isActive} onClick={() => setActiveId(study.id)} className={`flex w-full flex-col items-start gap-2 border-b border-line p-6 text-left transition-colors duration-150 ease-premium last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy lg:p-7 ${isActive ? 'bg-navy' : 'bg-white hover:bg-canvas'}`} data-id="element-155" data-mp="2cz1sln3pqnah">
                        <span className={`text-[11px] font-semibold uppercase tracking-label ${isActive ? 'text-metal-light' : 'text-charcoal-muted'}`} data-id="element-156" data-mp="1kbeduop4iqvr">
                          {study.company}
                        </span>
                        <span className={`font-serif text-[26px] font-semibold leading-none tracking-[-0.02em] ${isActive ? 'text-white' : 'text-navy'}`} data-id="element-157" data-mp="y2qv3nd1karb">
                          {study.metric}
                        </span>
                        <span className={`text-[15px] font-medium leading-snug ${isActive ? 'text-white/75' : 'text-charcoal'}`} data-id="element-158" data-mp="1lra1fxcj6dgr">
                          {study.headline}
                        </span>
                      </button>
                    </li>;
              })}
              </ul>

              <div className="p-8 lg:p-12" data-id="element-159" data-mp="1czz1jp3qfje">
                <AnimatePresence mode="wait" initial={false} data-id="element-160" data-mp="3sirdhltio3qd">
                  <motion.div key={active.id} initial={reduceMotion ? undefined : {
                  opacity: 0,
                  y: 10
                }} animate={reduceMotion ? undefined : {
                  opacity: 1,
                  y: 0
                }} exit={reduceMotion ? undefined : {
                  opacity: 0,
                  y: -6
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} data-id="element-161" data-mp="3sklwuvp53nsc">
                    <p className="text-[12px] font-medium uppercase tracking-label text-metal" data-id="element-162" data-mp="cr1o58uel68q">
                      {active.company} · {active.metricLabel}
                    </p>
                    <h3 className="mt-4 max-w-xl font-serif text-[26px] font-semibold leading-tight tracking-[-0.01em] text-navy lg:text-[30px]" data-id="element-163" data-mp="3g9bf3rml6iij">
                      {active.headline}
                    </h3>

                    <dl className="mt-9 space-y-7" data-id="element-164" data-mp="3pzwa7531x4wp">
                      {stages.map(stage => <div key={stage.key} className="border-t border-line pt-5" data-id="element-165" data-mp="2sifp901oxxdx">
                          <dt className="text-[11px] font-semibold uppercase tracking-label text-charcoal-muted" data-id="element-166" data-mp="2xgr0nf7mwfy9">
                            {stage.label}
                          </dt>
                          <dd className="mt-2.5 max-w-2xl text-[15px] leading-[1.7] text-charcoal-light" data-id="element-167" data-mp="1amdpyipnqobv">
                            {active[stage.key]}
                          </dd>
                        </div>)}
                    </dl>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}